import { z } from 'zod';

/**
 * Ticket-Status Enum.
 * Workflow: free → assigned → revoked
 */
export const TicketStatusSchema = z.enum(['free', 'assigned', 'revoked']);

/** TypeScript Type für Ticket-Status */
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

/**
 * Zod Schema für Ticket — Runtime-Validierung + Type-Inference.
 * Physisches PDF-Ticket (ticket.io) — nur für ICON_LEAGUE Events.
 */
export const TicketSchema = z.object({
  id: z.string().cuid(),
  eventId: z.string().cuid(),
  userId: z.string().nullable().optional(),
  reservationId: z.string().cuid().nullable().optional(),
  seatInfo: z.string().nullable().optional(), // Block/Reihe/Platz
  pdfUrl: z.string().url().nullable().optional(),
  pdfDriveId: z.string().nullable().optional(), // Google Drive File ID
  status: TicketStatusSchema.default('free'),
  assignedAt: z.coerce.date().nullable().optional(),
  revokedAt: z.coerce.date().nullable().optional(),
  revokeReason: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type Ticket = z.infer<typeof TicketSchema>;

/**
 * Schema für das Aktualisieren der internen Notizen eines Tickets.
 * Wird für PATCH /tickets/:id/notes genutzt.
 */
export const UpdateTicketNotesSchema = z.object({
  /** Interne Anmerkungen zum Ticket — null löscht die Notiz */
  notes: z.string().nullable(),
});

/** TypeScript Type für Notizen-Update eines Tickets */
export type UpdateTicketNotes = z.infer<typeof UpdateTicketNotesSchema>;

/**
 * Schema für Ticket-Erstellung.
 */
export const CreateTicketSchema = TicketSchema.pick({
  eventId: true,
  userId: true,
  reservationId: true,
  seatInfo: true,
  pdfUrl: true,
  pdfDriveId: true,
  status: true,
});

/** TypeScript Type für Ticket-Erstellung */
export type CreateTicket = z.infer<typeof CreateTicketSchema>;

/**
 * Schema für Ticket-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateTicketSchema = TicketSchema.pick({
  userId: true,
  reservationId: true,
  seatInfo: true,
  pdfUrl: true,
  pdfDriveId: true,
  status: true,
  assignedAt: true,
  revokedAt: true,
  revokeReason: true,
}).partial();

/** TypeScript Type für Ticket-Update */
export type UpdateTicket = z.infer<typeof UpdateTicketSchema>;

/**
 * Schema für ein einzelnes ticket.io Ticket-Item beim Import.
 * ticketId und barcode werden in seatInfo bzw. pdfDriveId gespeichert,
 * da das Prisma-Modell keine dedizierten Felder dafür hat.
 */
export const TicketIoItemSchema = z.object({
  /** Externe ticket.io Ticket-ID (z.B. "101980310") — wird in seatInfo gespeichert */
  ticketId: z.string().min(1),
  /** Barcode des Tickets (z.B. "L3HXX3BJ") — wird in pdfDriveId gespeichert */
  barcode: z.string().min(1),
  /** PDF-Dateiname (z.B. "Ticket-007.pdf") — wird in pdfUrl gespeichert */
  pdfFile: z.string().min(1),
});

/** TypeScript Type für ein ticket.io Ticket-Item */
export type TicketIoItem = z.infer<typeof TicketIoItemSchema>;

/**
 * Schema für den Ticket-Import via ticket.io Format.
 * Bulk-Import mehrerer Tickets für ein Event.
 */
export const ImportTicketsSchema = z.object({
  /** Event-ID, für die Tickets importiert werden */
  eventId: z.string().min(1),
  /** Array von ticket.io Ticket-Objekten */
  tickets: z.array(TicketIoItemSchema).min(1),
});

/** TypeScript Type für den Ticket-Import */
export type ImportTickets = z.infer<typeof ImportTicketsSchema>;

/**
 * Schema für den Bulk-Delete aller Tickets eines Events.
 */
export const DeleteEventTicketsSchema = z.object({
  /** Event-ID, deren Tickets gelöscht werden */
  eventId: z.string().min(1),
});

/** TypeScript Type für Bulk-Delete */
export type DeleteEventTickets = z.infer<typeof DeleteEventTicketsSchema>;

/**
 * Versandmodus für den Bulk-Ticket-Versand.
 * - `all`: Alle bestätigten Reservierungen (auch bereits versendete)
 * - `unsent`: Nur Reservierungen ohne zugewiesene Tickets
 */
export const SendTicketsModeSchema = z.enum(['all', 'unsent']);

/** TypeScript Type für den Versandmodus */
export type SendTicketsMode = z.infer<typeof SendTicketsModeSchema>;

/**
 * Request-Body Schema für den Bulk-Ticket-Versand.
 * Optionaler bodyOverride überschreibt den Template-Body für diesen Versand.
 */
export const SendTicketsRequestSchema = z.object({
  /** Optionaler Mail-Body der das Template überschreibt (max. 10.000 Zeichen) */
  bodyOverride: z.string().max(10000).optional(),
});

/** TypeScript Type für den Send-Tickets-Request */
export type SendTicketsRequest = z.infer<typeof SendTicketsRequestSchema>;

/**
 * Response-Schema für den Bulk-Ticket-Versand.
 */
export const SendTicketsResponseSchema = z.object({
  /** Anzahl erfolgreich versendeter Mails */
  sent: z.number(),
  /** Anzahl neu zugewiesener Tickets */
  assigned: z.number(),
  /** Fehlermeldungen pro fehlgeschlagener Reservierung */
  errors: z.array(z.string()),
  /** Gesamtanzahl der verarbeiteten Reservierungen */
  total: z.number(),
});

/** TypeScript Type für die Send-Tickets-Response */
export type SendTicketsResponse = z.infer<typeof SendTicketsResponseSchema>;

/**
 * Widerruf-Modus für den Bulk-Widerruf von Tickets.
 * - `all`: Alle zugewiesenen Tickets des Events widerrufen
 * - `count`: Eine zufällige Anzahl zugewiesener Tickets widerrufen
 * - `selected`: Nur die angegebenen Tickets widerrufen
 */
export const BulkRevokeModeSchema = z.enum(['all', 'count', 'selected']);

/** TypeScript Type für den Bulk-Widerruf-Modus */
export type BulkRevokeMode = z.infer<typeof BulkRevokeModeSchema>;

/**
 * Request-Body Schema für den Bulk-Widerruf von Tickets.
 */
export const BulkRevokeTicketsSchema = z
  .object({
    mode: BulkRevokeModeSchema,
    count: z.number().int().positive().optional(),
    ticketIds: z.array(z.string().min(1)).optional(),
    reason: z.string().min(5, 'Grund muss mindestens 5 Zeichen haben'),
  })
  .superRefine((val, ctx) => {
    if (val.mode === 'count' && (val.count === undefined || val.count < 1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['count'],
        message: 'count ist bei mode "count" erforderlich',
      });
    }
    if (val.mode === 'selected' && (!val.ticketIds || val.ticketIds.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ticketIds'],
        message: 'ticketIds ist bei mode "selected" erforderlich',
      });
    }
  });

/** TypeScript Type für den Bulk-Widerruf-Request */
export type BulkRevokeTickets = z.infer<typeof BulkRevokeTicketsSchema>;

/**
 * Response-Schema für den Bulk-Widerruf von Tickets.
 */
export const BulkRevokeTicketsResponseSchema = z.object({
  revokedCount: z.number(),
  mailsSent: z.number(),
});

/** TypeScript Type für die Bulk-Widerruf-Response */
export type BulkRevokeTicketsResponse = z.infer<typeof BulkRevokeTicketsResponseSchema>;
